import React from "react";
import { Helmet } from "react-helmet";
import Header from "../Header";
import Footer from "../Footer";
import "./GameUI.css";

export default function GameUI() {
    const services = [
        {
            image: "assets/img/GameDevelopment/Creative-UI-Character-Design.png",
            title: "Gameplay HUD Design",
            description: "Design readable HUDs for health, score, timer, abilities, inventory, turn status, objectives, and gameplay feedback.",
            points: ["HUD hierarchy", "Action feedback", "Readable states"]
        },
        {
            image: "assets/img/GameDevelopment/gameui/multiplayer.jpg",
            title: "Lobby & Multiplayer UI",
            description: "Create lobbies, game rooms, matchmaking, private tables, team screens, friend invites, and ready-state interfaces.",
            points: ["Matchmaking UI", "Room flows", "Player status"]
        },
        {
            image: "assets/img/GameDevelopment/mobileui/store.jpg",
            title: "Economy, Store & Wallet UI",
            description: "Design coins, gems, offers, bundles, store screens, wallets, rewards, purchase states, and transaction history flows.",
            points: ["Store screens", "Wallet flows", "Reward states"]
        },
        {
            image: "assets/img/GameDevelopment/mobileui/liveops.jpg",
            title: "LiveOps & Event UI",
            description: "Prepare UI for daily missions, streaks, battle passes, seasonal events, campaigns, push banners, and content updates.",
            points: ["Events UI", "Missions", "Remote content"]
        }
    ];

    const interfaceTypes = [
        { image: "assets/img/GameDevelopment/mobileui/gameplayHud.jpg", title: "Action HUD", description: "Controls, aiming, health, ammo, skills, enemy feedback, objective markers, and moment-to-moment clarity." },
        { image: "assets/img/GameDevelopment/mobileui/board.jpg", title: "Board Game UI", description: "Turn status, dice controls, player panels, room states, betting rules, and result screens." },
        { image: "assets/img/GameDevelopment/card-game1.png", title: "Card Game UI", description: "Table layout, card actions, timers, hand clarity, pot states, user badges, and round feedback." },
        { image: "assets/img/GameDevelopment/fantasy-cricket-app-img.png", title: "Fantasy UI", description: "Team builder, contests, wallet, lineup states, leaderboards, match center, and live scoring." },
        { image: "assets/img/GameDevelopment/quizappfeat.png", title: "Quiz Game UI", description: "Question states, timers, answer feedback, scoreboards, lifelines, rewards, and progression." },
        { image: "assets/img/GameDevelopment/services-vr-game-hero-1.jpg", title: "AR/VR Game UI", description: "Spatial UI, comfort-first interactions, controller prompts, world-space labels, and immersive feedback." }
    ];

    const gallery = [
        { image: "assets/img/GameDevelopment/Battle-Royale-Game-th.png", title: "Battle Game Interface" },
        { image: "assets/img/GameDevelopment/Arcade_Games_Development.png", title: "Arcade Game UI" },
        { image: "assets/img/GameDevelopment/Board-Game-Banner.png", title: "Board Game Interface" },
        { image: "assets/img/GameDevelopment/onlinr-casino.jpg", title: "Casino Game UI" },
        { image: "assets/img/GameDevelopment/endless-runner-game-thumbnail.png", title: "Runner Game UI" },
        { image: "assets/img/GameDevelopment/puzzle_feature_icon.png", title: "Puzzle UI Elements" },
        { image: "assets/img/GameDevelopment/Sports-Booking-App-India.png", title: "Sports Game UI" },
        { image: "assets/img/GameDevelopment/SCREEN4.png", title: "Gameplay Screen" }
    ];

    const deliverables = [
        "HUD Screens",
        "Menu Screens",
        "Lobby UI",
        "Store UI",
        "Wallet UI",
        "Reward UI",
        "Leaderboard UI",
        "Event UI",
        "UI Style Guide",
        "Icon Set",
        "Clickable Prototype",
        "Developer Handoff"
    ];

    const process = [
        { step: "01", title: "Game UX Audit", description: "We review game genre, player goals, controls, economy, retention loops, monetization, platforms, and UI complexity." },
        { step: "02", title: "Flow & Wireframes", description: "We plan menus, HUD placement, lobbies, stores, rewards, player states, edge cases, and core screen flows." },
        { step: "03", title: "Visual Direction", description: "We define UI style, color, typography, button states, icons, panels, animation direction, and screen hierarchy." },
        { step: "04", title: "Prototype & States", description: "We prepare clickable prototypes, interaction notes, loading states, empty states, success states, and error states." },
        { step: "05", title: "Handoff", description: "We deliver assets, UI kits, components, spacing rules, screen specs, export files, and implementation support." }
    ];

    const benefits = [
        { icon: "fa-solid fa-gamepad", title: "Built for Gameplay", description: "Game UI must support fast decisions, clear feedback, readable hierarchy, and repeated sessions." },
        { icon: "fa-solid fa-layer-group", title: "Reusable UI Systems", description: "Components, states, and styles help your team add new screens, events, and content faster." },
        { icon: "fa-solid fa-coins", title: "Economy-Aware Design", description: "Wallets, rewards, stores, offers, missions, and monetization flows are designed with player trust in mind." },
        { icon: "fa-solid fa-code", title: "Developer-Ready Assets", description: "We prepare specs, assets, states, and responsive guidance so implementation is smoother." }
    ];

    const faqs = [
        {
            question: "What game UI design services does Oengines provide?",
            answer: "Oengines designs game HUDs, menus, lobbies, multiplayer rooms, wallets, stores, rewards, leaderboards, LiveOps screens, prototypes, UI kits, and developer handoff files."
        },
        {
            question: "Can you design UI for mobile, PC, web, and console games?",
            answer: "Yes. We design game UI for mobile, web, PC, console, AR/VR, card games, board games, casual games, fantasy apps, and multiplayer products."
        },
        {
            question: "Do you create UI assets and icons?",
            answer: "Yes. We can create UI panels, buttons, icons, HUD assets, reward elements, store components, screen states, and style guides."
        },
        {
            question: "Can developers implement your game UI designs?",
            answer: "Yes. We provide structured handoff with components, assets, measurements, states, export files, and implementation notes."
        }
    ];

    const structuredData = {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "Game UI Design Services",
        "provider": {
            "@type": "Organization",
            "name": "Oengines Games LLP",
            "url": "https://www.oengines.com"
        },
        "serviceType": "Game UI UX Design",
        "areaServed": "Worldwide",
        "description": "Game UI design services for HUDs, menus, lobbies, wallets, rewards, stores, leaderboards, LiveOps, prototypes, UI kits, and developer handoff.",
        "offers": services.map((service) => ({
            "@type": "Offer",
            "itemOffered": {
                "@type": "Service",
                "name": service.title,
                "description": service.description
            }
        }))
    };

    return (
        <>
            <Helmet>
                <title>Game UI Design Services | HUD, Lobby, Store & LiveOps UI | Oengines</title>
                <meta name="description" content="Oengines Games LLP designs game UI for HUDs, menus, lobbies, multiplayer rooms, wallets, stores, rewards, leaderboards, LiveOps, prototypes, UI kits, and developer handoff." />
                <meta name="keywords" content="game UI design, game UX design, HUD design, game lobby UI, game menu design, mobile game UI, game interface design, LiveOps UI, game UI kit" />
                <link rel="canonical" href="https://www.oengines.com/game-ui-design" />
                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
            </Helmet>
            <Header />
            <main className="game-ui-page">
                <section className="game-ui-hero">
                    <div className="container">
                        <div className="row align-items-center g-4">
                            <div className="col-lg-7">
                                <span className="game-ui-eyebrow">Game UI/UX Design</span>
                                <h1>Design Game Interfaces That Improve Play, Retention & Monetization</h1>
                                <p>Oengines Games LLP designs HUDs, menus, lobbies, wallets, stores, rewards, leaderboards, event screens, prototypes, and UI systems for mobile, web, PC, console, and immersive games.</p>
                                <div className="game-ui-actions">
                                    <a href="/contact" className="as-btn">Start Game UI Design <i className="fa-regular fa-arrow-right ms-2"></i></a>
                                    <a href="tel:+912614054978" className="game-ui-call">Call: (0261) - 4054978</a>
                                </div>
                                <div className="game-ui-hero-stats">
                                    <div><strong>HUD</strong><span>Gameplay Clarity</span></div>
                                    <div><strong>Lobby</strong><span>Multiplayer Flow</span></div>
                                    <div><strong>LiveOps</strong><span>Events & Rewards</span></div>
                                </div>
                            </div>
                            <div className="col-lg-5">
                                <div className="game-ui-hero-gallery">
                                    <img className="game-ui-main-shot" src="assets/img/GameDevelopment/Battle-Royale-Game-th.png" alt="Game UI design" />
                                    <div className="game-ui-mini-grid">
                                        <img src="assets/img/GameDevelopment/Creative-UI-Character-Design.png" alt="Game UI art direction" />
                                        <img src="assets/img/GameDevelopment/Best-Ludo-Game.png" alt="Board game UI design" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="game-ui-service-section space">
                    <div className="container">
                        <div className="game-ui-heading text-center">
                            <span>Game UI Services</span>
                            <h2>Game Interface Design for Core Gameplay and Live Product Systems</h2>
                            <p>We design clear, production-ready interfaces for gameplay, player actions, social systems, stores, rewards, and ongoing content updates.</p>
                        </div>
                        <div className="row g-4">
                            {services.map((service) => (
                                <div className="col-lg-3 col-md-6" key={service.title}>
                                    <article className="game-ui-card">
                                        <img src={service.image} alt={service.title} />
                                        <div>
                                            <h3>{service.title}</h3>
                                            <p>{service.description}</p>
                                            <ul>
                                                {service.points.map((point) => <li key={point}>{point}</li>)}
                                            </ul>
                                        </div>
                                    </article>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="game-ui-interface-section space">
                    <div className="container">
                        <div className="game-ui-heading text-center">
                            <span>Interface Coverage</span>
                            <h2>Game UI Screens and Systems We Design</h2>
                            <p>Every game genre needs a different interface rhythm. We design around platform, session length, player intent, and moment-to-moment gameplay needs.</p>
                        </div>
                        <div className="game-ui-interface-grid">
                            {interfaceTypes.map((item) => (
                                <article className="game-ui-interface-card" key={item.title}>
                                    <img src={item.image} alt={item.title} />
                                    <h3>{item.title}</h3>
                                    <p>{item.description}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="game-ui-gallery-section space">
                    <div className="container">
                        <div className="game-ui-heading text-center">
                            <span>Visual Examples</span>
                            <h2>Image-Led UI Direction for Different Game Genres</h2>
                        </div>
                        <div className="game-ui-gallery-grid">
                            {gallery.map((item) => (
                                <figure className="game-ui-gallery-card" key={item.title}>
                                    <img src={item.image} alt={item.title} />
                                    <figcaption>{item.title}</figcaption>
                                </figure>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="game-ui-assets-section space">
                    <div className="container">
                        <div className="game-ui-assets-panel">
                            <div className="game-ui-heading mb-0">
                                <span>Design Deliverables</span>
                                <h2>UI Assets and Handoff for Game Development Teams</h2>
                                <p>We prepare interface assets, states, components, and notes so developers can implement game UI with fewer gaps.</p>
                            </div>
                            <div className="game-ui-assets-list">
                                {deliverables.map((asset) => <span key={asset}>{asset}</span>)}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="game-ui-process-section space">
                    <div className="container">
                        <div className="game-ui-heading text-center">
                            <span>Design Process</span>
                            <h2>From Game UX Audit to Developer-Ready UI Kit</h2>
                        </div>
                        <div className="game-ui-process-grid">
                            {process.map((item) => (
                                <article className="game-ui-process-card" key={item.step}>
                                    <span>{item.step}</span>
                                    <h3>{item.title}</h3>
                                    <p>{item.description}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="game-ui-benefit-section space">
                    <div className="container">
                        <div className="game-ui-heading text-center">
                            <span>Why Choose Oengines</span>
                            <h2>Game UI Design Backed by Product, Art & Engineering Awareness</h2>
                        </div>
                        <div className="row g-4">
                            {benefits.map((item) => (
                                <div className="col-lg-3 col-md-6" key={item.title}>
                                    <article className="game-ui-benefit-card">
                                        <span><i className={item.icon}></i></span>
                                        <h3>{item.title}</h3>
                                        <p>{item.description}</p>
                                    </article>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="game-ui-faq-section space">
                    <div className="container">
                        <div className="game-ui-heading text-center">
                            <span>FAQ</span>
                            <h2>Game UI Design Questions</h2>
                        </div>
                        <div className="game-ui-faq-grid">
                            {faqs.map((item) => (
                                <article className="game-ui-faq-card" key={item.question}>
                                    <h3>{item.question}</h3>
                                    <p>{item.answer}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="game-ui-cta">
                    <div className="container">
                        <div className="game-ui-cta-box">
                            <div>
                                <span>Design Better Game Interfaces</span>
                                <h2>Plan your game UI with Oengines.</h2>
                                <p>Share your game genre, platform, core screens, monetization flow, and player journey. We will help define a clear UI direction.</p>
                            </div>
                            <a href="/contact" className="as-btn">Book UI Consultation <i className="fa-regular fa-arrow-right ms-2"></i></a>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </>
    );
}
